import React, { useState, useEffect } from 'react';
import API_ENDPOINTS from '../../../core/constant/API_ENDPOINTS';
import DataTableWrapper from '../../admin/common/dataTable/dataTableWrapper';
import ConfirmActionModal from '../../components/modal/ConfirmActionModal';
import SuccessModal from '../../components/modal/SuccessModal';
import ErrorModal from '../../components/modal/ErrorModal';
import usePaginatedFetch from '../../../core/hooks/api/usePaginatedFetch';
import usePost from '../../../core/hooks/api/usePost';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import moment from 'moment';
import { AutoCompleteDropdown } from '../common/input/inputField';
import useFetch from '../../../core/hooks/api/useFetch';
import BaseModal from '../../components/modal/baseModal';
import { DatePicker } from '../../components/inputs';

const ActivatePackage = () => {
  const [selectedClass, setSelectedClass] = useState<any>(null);
  const [showDateModal, setShowDateModal] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [activationDate, setActivationDate] = useState<Date | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(0);
  const [userSuggestions, setUserSuggestions] = useState<Array<{ label: string; value: number | null }>>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserLabel, setSelectedUserLabel] = useState<string>('');
  const [showFooter, setShowFooter] = useState(false); // New state to manage footer visibility
  const { data: customers } = useFetch(`${API_ENDPOINTS.users}?fullName=&email=${searchQuery}&profile=customer&active=&birthdate=`);

  const { post, loading: loadingModal } = usePost(API_ENDPOINTS.paymentActivate);

  const params = selectedUserId
    ? { customerId: selectedUserId }
    : null;

  const {
    data,
    totalElements,
    first,
    rows,
    onPageChange,
    refetch
  } = usePaginatedFetch(selectedUserId ? API_ENDPOINTS.paymentDetails: null, 10, 0, params);

  useEffect(() => {
    if (customers) {
      const filtered = customers?.content?.filter((customer: { email: string }) =>
        customer.email.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setUserSuggestions(filtered?.map((customer: { email: string, id: string | number }) => ({
        label: customer.email,
        value: customer.id
      })));
    }
  }, [customers, searchQuery]);

  const handleUserChange = (value: string | null) => {
    const selectedUser = userSuggestions.find((option: { label: string, value: number | null }) => option.label === value);
    setSelectedUserId(selectedUser?.value || null);
    setSelectedUserLabel(value || '');
  };

  const handleSearchUser = (query: string) => {
    setSearchQuery(query);
  };

  const formatDateToLongSpanish = (date: Date | undefined): string => {
    if (!date) return '';
    const formattedDate = format(date, "EEEE dd 'de' MMMM 'del' yyyy", { locale: es });
    return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
  };

  const handleDateSelection = () => {
    if (!activationDate) {
      setErrorMessage('Por favor selecciona una fecha de activación.');
      return;
    }

    const today = moment().startOf('day');
    const selectedDate = moment(activationDate);

    if (selectedDate.isBefore(today)) {
      setErrorMessage('La fecha de activación debe ser hoy o una fecha futura.');
      return;
    }

    setShowDateModal(false);
    setShowSummaryModal(true);
    setErrorMessage(null);
  };

  const handleActivateClass = async () => {
    if (!selectedClass || !activationDate) {
      setErrorMessage('Por favor selecciona una fecha de activación.');
      return;
    }
    try {
      const payload = {
        userId: selectedUserId,
        packetId: selectedClass.packageId,
        quantity: selectedClass.quantity,
        activationDate: activationDate.toISOString().split('T')[0],
        paymentDetailId: selectedClass.paymentDetailId,
      };
      const response = await post(payload);
      setSuccessMessage(response.message || 'Clase activada exitosamente.');
      setShowSummaryModal(false);
      setShowSuccessModal(true);
      refetch();
      setErrorMessage(null);
    } catch (error: any) {
      console.error('Error al activar la clase:', error);
      setErrorModalMessage(error?.response?.data?.message || 'Hubo un error al activar la clase. Inténtalo nuevamente.');
      setShowErrorModal(true);
    } finally {
      setShowSummaryModal(false);
    }
  };

  const openDateModal = (rowData: any) => {
    const today = moment().startOf('day');
    setActivationDate(today.toDate());
    setSelectedClass(rowData);
    setShowDateModal(true);
    setErrorMessage(null);
  };

  const closeDateModal = () => {
    setShowFooter(false); // Reset footer visibility when closing the modal
    const today = moment().startOf('day'); // Use moment to reset to today's date
    setActivationDate(today.toDate());
    setShowDateModal(false);
    setShowSummaryModal(false);
    setErrorMessage(null);
  };

  const columns = [
    { field: 'paymentDate', header: 'Fecha de Pago', body: (rowData: any) => rowData.paymentDate },
    { field: 'description', header: 'Descripción', body: (rowData: any) => rowData.description },
    { field: 'customerName', header: 'Cliente', body: (rowData: any) => rowData.customerName },
    { field: 'customerEmail', header: 'Correo Electrónico', body: (rowData: any) => rowData.customerEmail },
    { field: 'quantity', header: 'Cantidad', body: (rowData: any) => rowData.quantity },
    { field: 'amount', header: 'Monto Total', body: (rowData: any) => `S/. ${Number(rowData.amount).toFixed(2)}` },
    {
      field: 'isActivated',
      header: 'Estado',
      className: 'text-center',
      body: (rowData: any) => (
        <div>
          {rowData.isActivated ? (
            <span className="badge badge-success">Activado</span>
          ) : (
            <button className="btn btn-primary btn-sm" onClick={() => openDateModal(rowData)}>
              Activar Paquete/Membresía
            </button>
          )}
        </div>
      ),
      style: { position: 'sticky', right: 0, backgroundColor: 'white', zIndex: 1 },
    },
  ];

  return (
    <>
    < div className="page-wrapper page-settings">
        <div className="content">
            <div className="d-flex w-100 mt-3 mb-3">
                <div className="row w-100">
                <div className="col">
                    <h4>Activar Paquete</h4>
                </div>
                </div>
            </div>
            <div className="row mb-3">
                <div className="col-12">
                <AutoCompleteDropdown
                    label="Seleccionar usuario"
                    value={selectedUserLabel}
                    suggestions={userSuggestions}
                    onChange={handleUserChange}
                    onSearch={handleSearchUser}
                    placeholder="Escribe para buscar..."
                />
                </div>
            </div>
            <div className="row">
                <div className="col-12">
                <DataTableWrapper
                    data={data || []}
                    columns={columns}
                    paginator
                    showGridlines
                    rowsPerPage={rows}
                    totalRecords={totalElements}
                    first={first}
                    onPageChange={onPageChange}
                    emptyMessage="No se encontraron detalles de pago."
                />
                </div>
            </div>
            <ConfirmActionModal
                show={showSummaryModal}
                onClose={closeDateModal}
                onConfirm={handleActivateClass}
                loading={loadingModal}
                centered
                title="Confirmar Activación"
            >
                <div className="text-center">
                <p className="mb-3">Tu clase se activará con la siguiente información:</p>
                <table className="table table-bordered">
                    <tbody>
                    <tr>
                        <th>Clase</th>
                        <td>{selectedClass?.description}</td>
                    </tr>
                    <tr>
                        <th>Monto</th>
                        <td>S/. {Number(selectedClass?.amount).toFixed(2)}</td>
                    </tr>
                    <tr>
                        <th>Fecha de Activación</th>
                        <td>{formatDateToLongSpanish(activationDate)}</td>
                    </tr>
                    <tr>
                        <th>Cliente</th>
                        <td>{selectedClass?.customerName}</td>
                    </tr>
                    <tr>
                        <th>Correo Electrónico</th>
                        <td>{selectedClass?.customerEmail}</td>
                    </tr>
                    </tbody>
                </table>
                {errorMessage && (
                    <div className="alert alert-danger mt-3" role="alert">
                    {errorMessage}
                    </div>
                )}
                </div>
            </ConfirmActionModal>

            <BaseModal
                show={showDateModal}
                onClose={closeDateModal}
                title="Seleccionar Fecha de Activación"
                centered
                footer={
                    showFooter && ( // Use showFooter to control footer visibility
                        <>
                        <button
                            onClick={closeDateModal}
                            className="btn btn-secondary"
                        >
                            Cancelar
                        </button>
                        <button onClick={handleDateSelection} className="btn btn-primary">
                            Confirmar Fecha Seleccionada
                        </button>
                        </>
                    )
                }
            >
                <div className="text-center">
                <p className="mb-3">¿Deseas activar la clase hoy o seleccionar una fecha personalizada?</p>
                { !showFooter && (
                    <div className="d-flex justify-content-center gap-3 mt-3">
                    <button
                        className="btn btn-success"
                        onClick={() => {
                        const today = moment().startOf('day');
                        setActivationDate(today.toDate());
                        setErrorMessage(null);
                        setShowFooter(false); // Hide footer for quick activation
                        setTimeout(() => handleDateSelection(), 0); // Ensure state is updated before calling handleDateSelection
                        }}
                    >
                        Activar Hoy
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={() => {
                        setActivationDate(undefined); // Clear the date for custom selection
                        setErrorMessage(null);
                        setShowFooter(true); // Show footer for custom date selection
                        }}
                    >
                        Seleccionar Fecha
                    </button>
                    </div>
                )}
                {showFooter && (
                    <div className="w-100 mt-3">
                    <DatePicker
                        value={activationDate}
                        onChange={(date) => setActivationDate(date)}
                        blockPastDates={true}
                    />
                  </div>
                )}
                {errorMessage && (
                    <div className="alert alert-danger mt-3" role="alert">
                    {errorMessage}
                    </div>
                )}
                </div>
            </BaseModal>

            <SuccessModal
                centered
                show={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
                title="Activación Exitosa"
                text={successMessage || 'La clase se activó correctamente.'}
                onConfirm={() => setShowSuccessModal(false)}
            />
            <ErrorModal
                centered
                show={showErrorModal}
                onClose={() => setShowErrorModal(false)}
                title="Error en la Activación"
                text={errorModalMessage || 'Hubo un error al activar la clase. Inténtalo nuevamente.'}
                onRetry={() => setShowErrorModal(false)}
            />
        </div>
    </div>
    </>
  );
};

export default ActivatePackage;